import { useState, useEffect, useRef } from "react";
import Image from "next/image";
import Link from "next/link";
import { fetchCategories } from "@/services/categoryApi";
import { HiMusicalNote } from "react-icons/hi2";
import { FaComputer, FaBullhorn } from "react-icons/fa6";
import { IoCameraSharp } from "react-icons/io5";
import { LiaChalkboardTeacherSolid } from "react-icons/lia";

const CategoryOne = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth);
  };

  const scroll = (direction) => {
    if (!scrollRef.current) return;
    const scrollAmount = 300;
    scrollRef.current.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  };


  useEffect(() => {
    const loadCategories = async () => {

      try {
        setLoading(true);
        const response = await fetchCategories();


        const categories = response.data || [];

        const sortedCategories = categories.sort((a, b) => {
          const lenA = Array.isArray(a.tags) ? a.tags.length : 0;
          const lenB = Array.isArray(b.tags) ? b.tags.length : 0;
          return lenB - lenA;
        });

        const limitedCategories = sortedCategories.slice(0, 20);

        console.log("Sorted categories by tag length:", limitedCategories);

        setCategories(limitedCategories);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadCategories();
  }, []);


  useEffect(() => {
    checkScroll();
    const handleResize = () => checkScroll();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, [categories]);

  if (loading) return <p>Loading...</p>;
  if (error) return <p>Error: {error}</p>;
  if (categories.length === 0) return <p>No categories available.</p>;

  const getCategoryIcon = (categoryName) => {
    const iconSize = 50;
    const style = { transition: "color 0.3s ease" };

    switch (categoryName) {
      case "Music":
        return (
          <HiMusicalNote
            size={iconSize}
            className="category-icon-react music-icon"
            style={{ ...style, color: "#6c5ce7" }}
          />
        );
      case "IT/Computer":
      case "IT / COMPUTER":
        return (
          <FaComputer
            size={iconSize}
            className="category-icon-react computer-icon"
            style={{ ...style, color: "#00b894" }}
          />
        );
      case "Photography":
      case "Photgraphy":
        return (
          <IoCameraSharp
            size={iconSize}
            className="category-icon-react camera-icon"
            style={{ ...style, color: "#0984e3" }}
          />
        );
      case "Marketing":
        return (
          <FaBullhorn
            size={iconSize}
            className="category-icon-react market-icon"
            style={{ ...style, color: "#e17055" }}
          />
        );
      case "Classes 1-10":
      case "Classes 1 - 10":
        return (
          <LiaChalkboardTeacherSolid
            size={iconSize}
            className="category-icon-react teacher-icon"
            style={{ ...style, color: "#d63031" }}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="position-relative py-4 d-flex justify-content-center align-items-center">

      <div
        className="position-relative"
        style={{
          width: "90%",
          overflow: "hidden",
        }}
      >
        <div
          ref={scrollRef}
          className="d-flex flex-row flex-nowrap gap-4"
          style={{
            overflowX: "auto",
            scrollBehavior: "smooth",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
          }}
          onScroll={checkScroll}
        >
          <style jsx>{`
          div::-webkit-scrollbar {
            display: none;
          }
          :global([data-theme="dark"]) .music-icon {
            color: #a29bfe !important;
          }
          :global([data-theme="dark"]) .computer-icon {
            color: #55efc4 !important;
          }
          :global([data-theme="dark"]) .camera-icon {
            color: #74b9ff !important;
          }
          :global([data-theme="dark"]) .market-icon {
            color: #fab1a0 !important;
          }
          :global([data-theme="dark"]) .teacher-icon {
            color: #ff7675 !important;
          }
        `}</style>

          {categories.map((item, index) => {
            const imageUrl = item.image
              ? item.image
              : "/images/category/categoryicon.png";

            return (
              <div
                key={item._id || index}
                className="card shadow-sm border-0 d-flex flex-column align-items-center justify-content-between bg-transparent"
                style={{
                  minWidth: "240px",
                  flex: "0 0 auto",
                  textAlign: "center",
                  borderRadius: "12px",
                  paddingBottom: "10px",
                  transition: "transform 0.3s ease",
                }}
              >
                <Link href={`/course-filter-one-toggle/${item._id}`}>
                  <div
                    className="d-flex align-items-center justify-content-center rounded-top"
                    style={{
                      height: "120px",
                      width: "100%",
                      cursor: "pointer",
                    }}
                  >
                    {getCategoryIcon(item.name) || (
                      <Image
                        src={imageUrl}
                        width={50}
                        height={50}
                        quality={100}
                        unoptimized
                        className="object-contain"
                        alt={`${item.name} Icon`}
                        onError={(e) => {
                          e.target.src = "/images/category/categoryicon.png";
                        }}
                      />
                    )}
                  </div>
                </Link>

                {/* Card Body */}
                <div className="card-body d-flex flex-column align-items-center justify-content-between mt-2 w-100">
                  <Link href={`/course-filter-one-toggle/${item._id}`}>
                    <h5
                      className="card-title fw-bold text-center"
                      style={{ cursor: "pointer" }}
                    >
                      {item.name}
                    </h5>
                  </Link>

                  <div className="d-flex flex-column align-items-center gap-2 w-100">
                    <Link
                      href={`/course-filter-one-toggle/${item._id}`}
                      className="text-dark text-muted text-decoration-none"
                    >
                      {item.courseCount} Course{item.courseCount !== 1 ? "s" : ""}
                    </Link>

                    <Link
                      href={`/jobs/${item._id}`}
                      className="text-dark text-muted text-decoration-none"
                    >
                      {item.jobCount} Job{item.jobCount !== 1 ? "s" : ""}
                    </Link>

                    <Link
                      href={`/events/${item._id}`}
                      className="text-dark text-muted text-decoration-none"
                    >
                      {item.eventCount} Event{item.eventCount !== 1 ? "s" : ""}
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>


      </div>

      <button
        className="btn btn-light shadow position-absolute top-50 start-0 translate-middle-y rounded-circle"
        onClick={() => scroll("left")}
        disabled={!canScrollLeft}
        style={{
          width: "40px",
          height: "40px",
          border: "none",
          zIndex: 1050,
          padding: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          opacity: canScrollLeft ? 1 : 0.4,
          cursor: canScrollLeft ? "pointer" : "not-allowed",
          transform: "translate(-50%, -50%)",
        }}
      >
        <i className="fas fa-chevron-left fs-5"></i>
      </button>


      <button
        className="btn btn-light shadow position-absolute top-50 end-0 translate-middle-y rounded-circle"
        onClick={() => scroll("right")}
        disabled={!canScrollRight}
        style={{
          width: "40px",
          height: "40px",
          border: "none",
          zIndex: 1050,
          padding: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          opacity: canScrollRight ? 1 : 0.4,
          cursor: canScrollRight ? "pointer" : "not-allowed",
          transform: "translate(50%, -50%)",
        }}
      >
        <i className="fas fa-chevron-right fs-5"></i>
      </button>
    </div>
  );

};

export default CategoryOne;
